import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  TableSortLabel
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useState, useMemo } from 'react'
import { type EnrollmentDetailsReportModel } from './EnrollmentDetailsReport.types'
import { NewEnrollmentStatus } from '../../core/types'
import * as XLSX from 'xlsx'

interface TableViewProps {
  reportData: EnrollmentDetailsReportModel[]
  loading: boolean
}

type SortDirection = 'asc' | 'desc'
type SortableField = keyof EnrollmentDetailsReportModel

export function EnrollmentDetailsReportTableView (props: TableViewProps): JSX.Element {
  const { reportData, loading } = props
  const [orderBy, setOrderBy] = useState<SortableField>('lastName')
  const [order, setOrder] = useState<SortDirection>('asc')
  const [orderGroup, setOrderGroup] = useState<number | null>(null)

  const formatDate = (dateString: string | null): string => {
    if (dateString == null) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const booleanDisplay = (value: boolean | number | null): JSX.Element => {
    if (value === true || value === 1) {
      return <TableCell sx={{ textAlign: 'center' }}>
        <CheckCircleIcon aria-label="Yes" sx={{ color: '#367575', fontSize: '18px' }} />
      </TableCell>
    } else {
      return <TableCell></TableCell>
    }
  }

  const formatBoolean = (value: boolean | number | null): string => {
    return value === true || value === 1 ? 'Yes' : 'No'
  }

  const maxGroup = Math.max(...reportData.map(o => o.groupNumber))

  const getStatusName = (status: number): string => {
    return NewEnrollmentStatus[status]
  }

  const handleRequestSort = (property: SortableField, group?: number): void => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setOrderGroup(group ?? null)
  }

  const sortedData = useMemo(() => {
    const comparator = (a: EnrollmentDetailsReportModel, b: EnrollmentDetailsReportModel): number => {
      let aValue: any = a[orderBy]
      let bValue: any = b[orderBy]

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // Convert dates to comparable values
      if (orderBy === 'dateOfBirth' || orderBy === 'startDate' || orderBy === 'exitDate' || orderBy === 'enrollmentDate') {
        aValue = aValue != null ? new Date(aValue).getTime() : 0
        bValue = bValue != null ? new Date(bValue).getTime() : 0
      }

      // Group comparison
      if (orderGroup != null) {
        if (a.groupNumber === orderGroup && b.groupNumber !== orderGroup) {
          return -1
        } else if (a.groupNumber !== orderGroup && b.groupNumber === orderGroup) {
          return 1
        }
      }

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.toLowerCase().localeCompare(bValue.toLowerCase())
      }

      // Numeric comparison
      if (aValue < bValue) {
        return -1
      } else if (aValue > bValue) {
        return 1
      } else {
        return 0
      }
    }

    const idList = [...reportData].sort((a, b) => {
      return order === 'desc' ? -comparator(a, b) : comparator(a, b)
    }).map((item, index) => (item.id))

    return new Set(idList) // adding items to a Set removes duplicates while preserving order
  }, [reportData, order, orderBy, orderGroup])

  const handleExportToExcel = (): void => {
    const headers = [
      'First Name',
      'Last Name',
      'Child ID',
      'UIC',
      'Date of Birth',
      'Ethnicity Code',
      'FPL Quintile',
      'Gender',
      'Address',
      'Unit Number',
      'City',
      'State',
      'ZIP',
      'Status',
      'Homeless',
      'Diagnosed Disability',
      'Challenging Behavior',
      'Home Language Not English',
      'Parent Low Education',
      'Abuse/Neglect',
      'Environmental Risk'
    ]

    for (let i = 1; i <= maxGroup; i++) {
      headers.push(`Service Provider ${i}`)
      headers.push(`Program ${i}`)
      headers.push(`Enroll Date ${i}`)
      headers.push(`End Date ${i}`)
    }

    const data: Array<Array<string | number | boolean>> = Array.from(sortedData).map(value => {
      const row = reportData.find(r => r.id === value && r.groupNumber === 1)
      if (row !== undefined) {
        const result: Array<string | number | boolean> = [
          row.firstName,
          row.lastName,
          row.id,
          row.uic ?? '',
          formatDate(row.dateOfBirth),
          row.ethnicityCode ?? '',
          row.fplQuintile ?? '',
          row.birthGender ?? '',
          row.address ?? '',
          row.unitNumber ?? '',
          row.city ?? '',
          row.state ?? '',
          row.zipCode ?? '',
          getStatusName(row.status),
          formatBoolean(row.currentlyHomeless),
          formatBoolean(row.diagnosedDisability),
          formatBoolean(row.challengingBehavior),
          formatBoolean(row.homeLanguageNotEnglish),
          formatBoolean(row.parentWithLowEducation),
          formatBoolean(row.abuseOrNeglect),
          formatBoolean(row.environmentalRisk)
        ]

        for (let i = 1; i <= maxGroup; i++) {
          const subrow = reportData.find(r => r.id === value && r.groupNumber === i)
          if (subrow !== undefined) {
            result.push(subrow.serviceProvider ?? '')
            result.push(subrow.program ?? '')
            result.push(formatDate(subrow.enrollmentDate))
            result.push(formatDate(subrow.exitDate))
          }
        }
        return result
      } else {
        return []
      }
    })

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Enrollment Details')

    XLSX.writeFile(workbook, `EnrollmentDetailsReport_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (reportData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No enrollment records found matching the selected criteria.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
        <Chip
          label={`${sortedData.size} ${sortedData.size === 1 ? 'child' : 'children'}`}
          sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold' }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
        >
          Download Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: '70vh' }}>
        <Table stickyHeader size="small" className="report-table">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'lastName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'lastName'}
                  direction={orderBy === 'lastName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('lastName') }}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'firstName'}
                  direction={orderBy === 'firstName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('firstName') }}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => { handleRequestSort('id') }}
                >
                  Child ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'uic' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'uic'}
                  direction={orderBy === 'uic' ? order : 'asc'}
                  onClick={() => { handleRequestSort('uic') }}
                >
                  UIC
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'dateOfBirth' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'dateOfBirth'}
                  direction={orderBy === 'dateOfBirth' ? order : 'asc'}
                  onClick={() => { handleRequestSort('dateOfBirth') }}
                >
                  DOB
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'ethnicityCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ethnicityCode'}
                  direction={orderBy === 'ethnicityCode' ? order : 'asc'}
                  onClick={() => { handleRequestSort('ethnicityCode') }}
                >
                  Ethnicity
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'fplQuintile' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'fplQuintile'}
                  direction={orderBy === 'fplQuintile' ? order : 'asc'}
                  onClick={() => { handleRequestSort('fplQuintile') }}
                >
                  FPL Quintile
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'birthGender' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'birthGender'}
                  direction={orderBy === 'birthGender' ? order : 'asc'}
                  onClick={() => { handleRequestSort('birthGender') }}
                >
                  Gender
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'address' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'address'}
                  direction={orderBy === 'address' ? order : 'asc'}
                  onClick={() => { handleRequestSort('address') }}
                >
                  Address
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'unitNumber' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'unitNumber'}
                  direction={orderBy === 'unitNumber' ? order : 'asc'}
                  onClick={() => { handleRequestSort('unitNumber') }}
                >
                  Unit Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'city' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'city'}
                  direction={orderBy === 'city' ? order : 'asc'}
                  onClick={() => { handleRequestSort('city') }}
                >
                  City
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'state' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'state'}
                  direction={orderBy === 'state' ? order : 'asc'}
                  onClick={() => { handleRequestSort('state') }}
                >
                  State
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'zipCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'zipCode'}
                  direction={orderBy === 'zipCode' ? order : 'asc'}
                  onClick={() => { handleRequestSort('zipCode') }}
                >
                  ZIP
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => { handleRequestSort('status') }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'currentlyHomeless' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'currentlyHomeless'}
                  direction={orderBy === 'currentlyHomeless' ? order : 'asc'}
                  onClick={() => { handleRequestSort('currentlyHomeless') }}
                >
                  Homeless
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'diagnosedDisability' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'diagnosedDisability'}
                  direction={orderBy === 'diagnosedDisability' ? order : 'asc'}
                  onClick={() => { handleRequestSort('diagnosedDisability') }}
                >
                  Diagnosed Disability
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'challengingBehavior' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'challengingBehavior'}
                  direction={orderBy === 'challengingBehavior' ? order : 'asc'}
                  onClick={() => { handleRequestSort('challengingBehavior') }}
                >
                  Challenging Behavior
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'homeLanguageNotEnglish' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'homeLanguageNotEnglish'}
                  direction={orderBy === 'homeLanguageNotEnglish' ? order : 'asc'}
                  onClick={() => { handleRequestSort('homeLanguageNotEnglish') }}
                >
                  Lang Not English
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'parentWithLowEducation' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'parentWithLowEducation'}
                  direction={orderBy === 'parentWithLowEducation' ? order : 'asc'}
                  onClick={() => { handleRequestSort('parentWithLowEducation') }}
                >
                  Parent Low Ed
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'abuseOrNeglect' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'abuseOrNeglect'}
                  direction={orderBy === 'abuseOrNeglect' ? order : 'asc'}
                  onClick={() => { handleRequestSort('abuseOrNeglect') }}
                >
                  Abuse/Neglect
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'environmentalRisk' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'environmentalRisk'}
                  direction={orderBy === 'environmentalRisk' ? order : 'asc'}
                  onClick={() => { handleRequestSort('environmentalRisk') }}
                >
                  Env Risk
                </TableSortLabel>
              </TableCell>
              { /* add a set of columns for each group */}
              {Array.from({ length: maxGroup }, (_, i) => {
                const group = i + 1
                return <>
                  <TableCell sortDirection={orderBy === 'serviceProvider' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'serviceProvider' && group === orderGroup}
                      direction={orderBy === 'serviceProvider' ? order : 'asc'}
                      onClick={() => { handleRequestSort('serviceProvider', group) }}
                    >
                      Service Provider {group}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'program' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'program' && group === orderGroup}
                      direction={orderBy === 'program' ? order : 'asc'}
                      onClick={() => { handleRequestSort('program', group) }}
                    >
                      Program {group}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'enrollmentDate' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'enrollmentDate' && group === orderGroup}
                      direction={orderBy === 'enrollmentDate' ? order : 'asc'}
                      onClick={() => { handleRequestSort('enrollmentDate', group) }}
                    >
                      Enrollment Date {group}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'exitDate' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'exitDate' && group === orderGroup}
                      direction={orderBy === 'exitDate' ? order : 'asc'}
                      onClick={() => { handleRequestSort('exitDate', group) }}
                    >
                      Exit Date {group}
                    </TableSortLabel>
                  </TableCell>
                </>
              })}

            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(sortedData).map(value => {
              const row = reportData.find(r => r.id === value && r.groupNumber === 1)
              if (row === undefined) return null
              return <TableRow key={row.id} hover>
                <TableCell>{row.lastName}</TableCell>
                <TableCell>{row.firstName} </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.uic}</TableCell>
                <TableCell>{formatDate(row.dateOfBirth)}</TableCell>
                <TableCell>{row.ethnicityCode}</TableCell>
                <TableCell>{row.fplQuintile}</TableCell>
                <TableCell>{row.birthGender}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell>{row.unitNumber ?? ''}</TableCell>
                <TableCell>{row.city}</TableCell>
                <TableCell>{row.state}</TableCell>
                <TableCell>{row.zipCode}</TableCell>
                <TableCell>{getStatusName(row.status)}</TableCell>
                {booleanDisplay(row.currentlyHomeless)}
                {booleanDisplay(row.diagnosedDisability)}
                {booleanDisplay(row.challengingBehavior)}
                {booleanDisplay(row.homeLanguageNotEnglish)}
                {booleanDisplay(row.parentWithLowEducation)}
                {booleanDisplay(row.abuseOrNeglect)}
                {booleanDisplay(row.environmentalRisk)}
                {Array.from({ length: maxGroup }, (_, i) => {
                  const subrow = reportData.find(r => r.id === value && r.groupNumber === i + 1)
                  if (subrow === undefined) {
                    return <>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </>
                  } else {
                    return <>
                      <TableCell>{subrow.serviceProvider}</TableCell>
                      <TableCell>{subrow.program}</TableCell>
                      <TableCell>{formatDate(subrow.enrollmentDate)}</TableCell>
                      <TableCell>{formatDate(subrow.exitDate)}</TableCell>
                    </>
                  }
                })}
              </TableRow>
            })}

          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
